import { useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useTenantContext } from '../../../TenantContext';
import { EmployerBusinessDetails } from '../../AddEmployerModal/EmployerBusinessDetails';
import { buildPayload } from '../../AddEmployerModal/payload';
import { emptyBusinessDetails, emptyPlacements } from '../../AddEmployerModal/types';
import type { SelectedEmployer } from './SelectEmployer';

export function AddEmployerInline({
  courseId,
  onAdded,
  onCancel,
}: {
  courseId: string;
  onAdded: (employer: SelectedEmployer) => void;
  onCancel: () => void;
}): React.ReactElement {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const tenant = useTenantContext();
  const addInFlight = useRef(false);

  const createEmployer = trpcAdmin.workPlacements.employers.createEmployer.useMutation({
    onSuccess: (createdEmployer) => {
      toast({ status: 'success', title: t`Employer added` });
      void utils.workPlacements.employers.listEmployers.invalidate();
      onAdded({
        id: createdEmployer.id,
        name: createdEmployer.name,
        locality: createdEmployer.addressLocality,
        state: createdEmployer.addressState,
      });
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to add employer`, description: error.message });
    },
    onSettled: () => {
      addInFlight.current = false;
    },
  });

  return (
    <EmployerBusinessDetails
      business={emptyBusinessDetails}
      address={null}
      googleApiKey={tenant?.apis.GOOGLE_ADDRESS_VALIDATION_API_KEY ?? ''}
      onNext={(business, address) => {
        if (addInFlight.current) {
          return;
        }
        addInFlight.current = true;
        createEmployer.mutate(
          buildPayload(business, address, emptyPlacements, new Set([courseId])),
        );
      }}
      onCancel={onCancel}
      isSaving={createEmployer.isPending}
    />
  );
}
